import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, KeyRound, Building2, Calendar, Mail, Phone, ChevronRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getDscBook } from "@/lib/api";
import { formatIndianDate } from "@/lib/date";

/**
 * DSC Book — cross-firm consolidated DSC register.
 *
 * Left column: Companies sorted by expiry urgency (expired first, then
 * expiring within 5 days, then by name).
 * Right column: DSC details for the selected company.
 *
 * Top banner shows a firm-wide expiry summary + clickable alerts for
 * every DSC expiring within 5 days or already expired.
 *
 * To add / edit individual DSC entries the user still goes to
 *   Companies → {company} → Registers → DSC Book
 * (the cross-company view is read-only on purpose so the per-company
 *  audit trail stays clean).
 */
export default function DscBook() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => {
    setLoading(true);
    getDscBook()
      .then((r) => {
        setData(r.data);
        // Auto-select the first company that has DSCs (or the first one in list)
        const firstWithDsc = r.data?.companies?.find((c) => c.dsc_count > 0);
        setSelectedId(firstWithDsc?.company_id || r.data?.companies?.[0]?.company_id || null);
      })
      .catch(() => toast.error("Failed to load DSC Book"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const selectedCompany = useMemo(
    () => data?.companies?.find((c) => c.company_id === selectedId),
    [data, selectedId],
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dsc-book-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
            <KeyRound size={14} className="text-[#002FA7]" /> DSC Book
          </div>
          <h1 className="font-semibold text-2xl sm:text-3xl tracking-tight mt-2 text-zinc-950">
            Digital Signature Certificates — Firm-wide register
          </h1>
          <p className="mt-1 text-sm text-zinc-600 max-w-2xl">
            All DSCs across all your companies in one place. Expiry alerts trigger 5 days before — renew with
            the issuing Certifying Authority before MCA V3 filings start failing.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[#002FA7] px-3 py-1.5 border border-zinc-200 hover:border-[#002FA7]"
          data-testid="dsc-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary strip */}
      {data?.summary && <SummaryStrip s={data.summary} />}

      {/* Expiry alerts */}
      {data?.expiry_alerts?.length > 0 && (
        <ExpiryAlerts alerts={data.expiry_alerts} onPick={(cid) => setSelectedId(cid)} />
      )}

      {loading && <div className="text-sm text-zinc-500 py-10 text-center" data-testid="dsc-loading">Loading…</div>}

      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Companies column */}
          <aside className="lg:col-span-4 border border-zinc-200" data-testid="dsc-companies-column">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
              Companies ({data.companies?.length || 0})
            </div>
            <ul className="divide-y divide-zinc-100 max-h-[600px] overflow-y-auto">
              {data.companies?.map((c) => (
                <CompanyRow
                  key={c.company_id}
                  company={c}
                  active={c.company_id === selectedId}
                  onClick={() => setSelectedId(c.company_id)}
                />
              ))}
            </ul>
          </aside>

          {/* DSC details column */}
          <section className="lg:col-span-8" data-testid="dsc-details-column">
            {selectedCompany ? (
              <DscDetailsPanel company={selectedCompany} />
            ) : (
              <div className="text-sm text-zinc-500 italic py-10 text-center border border-zinc-200">
                Select a company to view its DSCs
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function SummaryStrip({ s }) {
  const items = [
    { label: "Companies", value: s.total_companies, testid: "summary-companies" },
    { label: "DSCs on record", value: s.total_dscs, testid: "summary-dscs" },
    { label: "Expired", value: s.total_expired, accent: s.total_expired > 0 ? "red" : "default", testid: "summary-expired" },
    { label: "Expiring in 5 days", value: s.total_expiring_in_5_days, accent: s.total_expiring_in_5_days > 0 ? "amber" : "default", testid: "summary-5d" },
    { label: "Expiring in 30 days", value: s.total_expiring_in_30_days, testid: "summary-30d" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="dsc-summary-strip">
      {items.map((it) => (
        <div
          key={it.label}
          className={`p-4 border ${
            it.accent === "red"
              ? "border-red-200 bg-red-50"
              : it.accent === "amber"
              ? "border-amber-200 bg-amber-50"
              : "border-zinc-200 bg-white"
          }`}
          data-testid={it.testid}
        >
          <div
            className={`text-2xl font-light ${
              it.accent === "red"
                ? "text-red-700"
                : it.accent === "amber"
                ? "text-amber-700"
                : "text-zinc-900"
            }`}
          >
            {it.value ?? 0}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-zinc-600 mt-1">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function ExpiryAlerts({ alerts, onPick }) {
  return (
    <div className="mb-6 border border-amber-300 bg-amber-50 p-4" data-testid="dsc-expiry-alerts">
      <div className="flex items-center gap-2 text-amber-800 mb-3">
        <AlertTriangle size={16} />
        <span className="font-medium text-sm">
          {alerts.length} DSC{alerts.length > 1 ? "s" : ""} need attention
        </span>
      </div>
      <ul className="space-y-1.5">
        {alerts.slice(0, 10).map((a, idx) => (
          <li key={`${a.id}-${idx}`} data-testid={`dsc-alert-${idx}`}>
            <button
              onClick={() => onPick(a.company_id)}
              className="text-left text-sm hover:underline w-full flex items-center justify-between"
            >
              <span>
                <span
                  className={`inline-block px-1.5 py-0.5 mr-2 text-[10px] font-medium uppercase tracking-wider ${
                    a.status_flag === "expired"
                      ? "bg-red-200 text-red-900"
                      : "bg-amber-200 text-amber-900"
                  }`}
                >
                  {a.status_flag === "expired" ? "Expired" : `${a.days_to_expiry}d`}
                </span>
                <span className="font-medium">{a.holder_name || "—"}</span>
                <span className="text-zinc-500"> · {a.company_name}</span>
                <span className="text-zinc-500"> · exp {formatIndianDate(a.expiry_date || "")}</span>
              </span>
              <ChevronRight size={14} className="text-zinc-400" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompanyRow({ company, active, onClick }) {
  const hasAlerts = (company.expired_count || 0) + (company.expiring_soon_count || 0) > 0;
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors flex items-start gap-3 ${
          active ? "bg-[#002FA7]/5 border-l-2 border-l-[#002FA7]" : ""
        }`}
        data-testid={`company-row-${company.company_id}`}
      >
        <Building2 size={16} className="text-zinc-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-zinc-900 truncate">
            {company.company_name || "—"}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
            {company.cin || "No CIN"}
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-[11px]">
            <span className="text-zinc-600">{company.dsc_count} DSC{company.dsc_count !== 1 ? "s" : ""}</span>
            {company.expired_count > 0 && (
              <span className="px-1.5 py-0.5 bg-red-100 text-red-900 font-medium uppercase tracking-wider">
                {company.expired_count} expired
              </span>
            )}
            {company.expiring_soon_count > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 font-medium uppercase tracking-wider">
                {company.expiring_soon_count} due in 5d
              </span>
            )}
          </div>
        </div>
        {hasAlerts && <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />}
      </button>
    </li>
  );
}

function DscDetailsPanel({ company }) {
  return (
    <div className="border border-zinc-200">
      <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50 flex items-start justify-between">
        <div>
          <div className="font-semibold text-base text-zinc-900">{company.company_name || "—"}</div>
          <div className="text-xs text-zinc-500 font-mono mt-0.5">{company.cin || "No CIN on record"}</div>
        </div>
        <Link
          to={`/companies/${company.company_id}/registers`}
          className="text-xs text-[#002FA7] hover:underline whitespace-nowrap"
          data-testid="open-register-link"
        >
          Open DSC register →
        </Link>
      </div>

      {company.dscs?.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-zinc-500 italic" data-testid="no-dscs">
          No DSCs on record for this company.
          <br />
          <Link
            to={`/companies/${company.company_id}/registers`}
            className="inline-block mt-3 text-[#002FA7] hover:underline"
          >
            Add the first DSC entry →
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dsc-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-600">
                <th className="px-4 py-3">Holder</th>
                <th className="px-4 py-3">DIN / PAN</th>
                <th className="px-4 py-3">Class / CA</th>
                <th className="px-4 py-3">Expiry</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {company.dscs.map((d) => (
                <tr key={d.id} data-testid={`dsc-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{d.holder_name || "—"}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                      {d.registered_email && (
                        <span className="flex items-center gap-1"><Mail size={10} />{d.registered_email}</span>
                      )}
                      {d.registered_mobile && (
                        <span className="flex items-center gap-1"><Phone size={10} />{d.registered_mobile}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{d.din_or_pan || "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-700">
                    <div>{d.dsc_class || "—"}</div>
                    <div className="text-[11px] text-zinc-500">{d.certifying_authority || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={11} className="text-zinc-400" />
                      <span>{formatIndianDate(d.expiry_date || "") || "—"}</span>
                    </div>
                    {typeof d.days_to_expiry === "number" && (
                      <div
                        className={`text-[11px] mt-0.5 ${
                          d.days_to_expiry < 0
                            ? "text-red-700"
                            : d.days_to_expiry <= 5
                            ? "text-amber-700"
                            : "text-zinc-500"
                        }`}
                      >
                        {d.days_to_expiry < 0
                          ? `${Math.abs(d.days_to_expiry)} days ago`
                          : `in ${d.days_to_expiry} days`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge flag={d.status_flag} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ flag }) {
  const map = {
    expired: { bg: "bg-red-100", text: "text-red-900", label: "Expired" },
    expiring_in_5_days: { bg: "bg-amber-100", text: "text-amber-900", label: "Expiring soon" },
    expiring_in_30_days: { bg: "bg-blue-100", text: "text-blue-900", label: "Due in 30 days" },
    active: { bg: "bg-emerald-100", text: "text-emerald-900", label: "Active" },
    unknown: { bg: "bg-zinc-100", text: "text-zinc-700", label: "Unknown" },
  };
  const cfg = map[flag] || map.unknown;
  return (
    <span className={`px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}
